import React from "react";
import { useSearchParams } from "react-router-dom";
import { zonedTimeToUtc } from 'date-fns-tz';
import useSwr from 'swr';
import {
    List,
    Row,
    Col,
    Flex,
    Image,
    Typography,
    Tag
} from 'antd';
import AIExpertHelpService from "../../../API/AIExpertHelpService";
import { getQueryFromPath } from '../../../utils/query';
import Markdown from 'react-markdown'

const { Text, Paragraph } = Typography;

const stateTagColorMap = {
    new: 'blue',
    done: 'green',
    error: 'red',
    draft: 'cyan',
    enqueued: 'orange',
    processing: 'gold',
    ready: 'purple',
    deleted: 'magenta',
};

export async function fetcher(url) {
    const query = getQueryFromPath(url);

    const { data } = await AIExpertHelpService.getAllUserRequests({
        limit: query.limit || 10,
        offset: query.offset || 0,
        sort_by: query.sort_by || 'created_at',
        sort_direction: query.sort_direction || 'descend',
        date_from: query.date_from || undefined,
        date_to: query.date_to || undefined,
        state: query.state || undefined,
    });

    return data;
}

const UserRequestsList = () => {
    let [searchParams, setSearchParams] = useSearchParams();

    const patchSearchParams = (newParams = {}) => {
        setSearchParams((searchParams) => {
            const prevParams = Object.fromEntries([...searchParams]);

            return { ...prevParams, ...newParams };
        });
    };

    const { data, loading } = useSwr(`/api/ai_expert_help/user_requests?${searchParams.toString()}`, fetcher, {
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        revalidateOnFocus: false,
        keepPreviousData: true,
    });

    const paginationParams = {
        current: (data?.offset / data?.limit + 1) || 1,
        pageSize: data?.limit || 10,
        total: data?.count,
        onChange: (pageNumber, pageSize) => {
            patchSearchParams({
                limit: pageSize,
                offset: pageSize * (pageNumber - 1),
            });
        },
    };

    const renderListItem = (item) => {
        return (
            <List.Item style={{ paddingBlock: 24 }}>
                <Row wrap={false} style={{ gap: 16 }}>
                    <Col flex="88px">
                        <Flex justify="center">
                            <Image
                                src={item.img_url}
                                style={{ maxHeight: 88, objectFit: 'contain' }}
                                fallback="data:image/png;base64,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"
                            />
                        </Flex>
                    </Col>
                    <Col flex="auto" style={{ minWidth: 250 }}>
                        <Text strong>Bot Response:</Text>
                        <Markdown
                            style={{ marginBottom: 0 }}
                        >
                            {item.bot_response ?? '-'}
                        </Markdown>
                    </Col>
                    <Col flex="250px">
                        <Flex justify="space-between">
                            <Text strong>State:</Text>
                            <Tag
                                color={stateTagColorMap[item.state] ?? 'default'}
                                style={{ marginRight: 0, textTransform: 'capitalize' }}
                            >
                                {item.state}
                            </Tag>
                        </Flex>
                        <Flex justify="space-between">
                            <Text strong>Created at:</Text>
                            {zonedTimeToUtc(item.created_at).toLocaleString()}
                        </Flex>
                        <Flex justify="space-between">
                            <Text strong>Updated at:</Text>
                            {item.updated_at ? zonedTimeToUtc(item.updated_at).toLocaleString() : '-'}
                        </Flex>
                        <Flex justify="space-between">
                            <Text strong>Completed at:</Text>
                            {item.completed_at ? zonedTimeToUtc(item.completed_at).toLocaleString() : '-'}
                        </Flex>
                    </Col>
                </Row>
            </List.Item>
        );
    };

    return (
        <List
            itemLayout="vertical"
            dataSource={data?.results}
            rowKey={(item) => item.request_id}
            loading={loading}
            pagination={paginationParams}
            style={{ overflowX: 'auto', overflowY: 'hidden' }}
            renderItem={renderListItem}
        />
    );
};

export default UserRequestsList;
